import { FolderInput } from "lucide-react";
import { DOC_PERMISSION_RANK, type DocFolderWithAccess } from "@estelle/domain";
import type { WriteRejection } from "@estelle/domain";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { FolderTree } from "@/modules/docs/screens/FolderTree";
import { RejectionNotice } from "@/modules/docs/screens/RejectionNotice";

/**
 * What "Move to…" has picked so far — a tagged union rather than a bare
 * `string | "root" | null`, since a folder id is itself a `string` and
 * would swallow a `"root"` literal into the same type, leaving nothing to
 * tell "the top level" and "a folder that happens to be named root" apart.
 */
export type MoveFolderTarget =
  { kind: "none" } | { kind: "root" } | { kind: "folder"; id: string };

/**
 * The folder picker behind a folder's own "Move to…" (FR-DOC-10, #414),
 * `moveFolder`'s counterpart to `MoveToFolderDialog` for pages.
 *
 * Two things a page move never has to deal with: the folder itself and
 * every one of its descendants have to be unpickable — moving a folder into
 * its own child is the cycle `moveFolder` refuses by materialised path
 * (`target.path.startsWith(`${folder.path}/`)`), and offering it here would
 * just spend a click on a rejection that was already knowable client-side.
 * And a folder can move to the top level, where a page cannot — `parentId`
 * is nullable, `folderId` is not — so this dialog offers "Top level" as a
 * standing option alongside the tree rather than only ever leaving one.
 *
 * "Top level" is offered whether or not the caller holds the permission
 * administration `moveFolder` actually requires for it — a courtesy filter,
 * not the enforcement, same as everywhere else this pattern shows up: the
 * API's own rejection through `RejectionNotice` is what a Write-only holder
 * actually sees, the same one a non-admin gets from `createFolder`'s root
 * branch.
 */
export function MoveFolderDialog({
  open,
  onOpenChange,
  folders,
  movingFolder,
  selectedTarget,
  onChangeSelectedTarget,
  pending,
  rejection,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folders: readonly DocFolderWithAccess[];
  movingFolder: DocFolderWithAccess;
  selectedTarget: MoveFolderTarget;
  onChangeSelectedTarget: (target: MoveFolderTarget) => void;
  pending: boolean;
  rejection: WriteRejection | null;
  onSubmit: (parentId: string | null) => void;
}) {
  // The folder itself and everything under it, by materialised path — the
  // same comparison `moveFolder` uses server-side, so nothing offered here
  // could come back as the cycle rejection.
  const excluded = new Set(
    folders
      .filter(
        (folder) =>
          folder.id === movingFolder.id ||
          folder.path.startsWith(`${movingFolder.path}/`),
      )
      .map((folder) => folder.id),
  );

  const pickableFolders = folders.filter(
    (folder) =>
      !excluded.has(folder.id) &&
      DOC_PERMISSION_RANK[folder.effectiveLevel] >= DOC_PERMISSION_RANK.write,
  );

  // Already at the top level — offering "Top level" would offer the move
  // it is already in, the same reason `MoveToFolderDialog` drops
  // `currentFolderId` from its own list.
  const canGoTopLevel = movingFolder.parentId !== null;

  const nothingToOffer = !canGoTopLevel && pickableFolders.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Move to…">
        <div className="space-y-3">
          {nothingToOffer ? (
            <p className="text-muted-foreground text-sm">
              There is nowhere else you can write to move this into.
            </p>
          ) : (
            <div className="space-y-1">
              {canGoTopLevel ? (
                <button
                  type="button"
                  onClick={() => {
                    onChangeSelectedTarget({ kind: "root" });
                  }}
                  className={cn(
                    "flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm",
                    "hover:bg-muted/50",
                    selectedTarget.kind === "root" && "bg-accent",
                  )}
                >
                  <FolderInput
                    className="text-muted-foreground size-4 shrink-0"
                    aria-hidden
                  />
                  Top level
                </button>
              ) : null}

              {pickableFolders.length > 0 ? (
                <div className="max-h-64 overflow-auto rounded-md border p-2">
                  <FolderTree
                    folders={pickableFolders}
                    selectedFolderId={
                      selectedTarget.kind === "folder"
                        ? selectedTarget.id
                        : null
                    }
                    onSelectFolder={(id) => {
                      onChangeSelectedTarget({ kind: "folder", id });
                    }}
                  />
                </div>
              ) : null}
            </div>
          )}

          {rejection ? <RejectionNotice rejection={rejection} /> : null}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={selectedTarget.kind === "none" || pending}
              onClick={() => {
                if (selectedTarget.kind === "root") {
                  onSubmit(null);
                } else if (selectedTarget.kind === "folder") {
                  onSubmit(selectedTarget.id);
                }
              }}
            >
              Move
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
